import React, { createContext, useContext, useEffect, useState } from 'react';
import { Box } from '@heycater/design-system';
import { BoxProps } from '@material-ui/core';
import styled from 'styled-components';

interface Section {
  id: string;
  label: string;
}
interface PageSectionContext {
  activeSection: Section | null;
  sections: Section[];
  addSection: ({ id, label }: Section) => void;
  topOffset: number;
}

const PageSectionContext = createContext<null | PageSectionContext>(null);

export const PageSectionProvider: React.FC<{ pageTopOffset?: number }> = ({
  children,
  pageTopOffset = 160,
}) => {
  const [activeSection, setActiveSection] = useState<Section | null>(null);
  const [trackedSections, setTrackedSections] = useState<Section[]>([]);

  const addSection = ({ id, label }: Section) =>
    setTrackedSections((prevSections) => {
      const sectionAlreadyTracked = prevSections.find(
        (prevSection) => prevSection.id === id
      );
      if (sectionAlreadyTracked) {
        return prevSections;
      } else {
        return [...prevSections, { id, label }];
      }
    });

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.pageYOffset;
      let activeSection = null;

      trackedSections.forEach((section) => {
        const sectionElement = document.getElementById(section.id);
        if (sectionElement) {
          const { offsetTop, offsetHeight } = sectionElement;

          const allowance = 30;

          if (
            currentPosition >= offsetTop - pageTopOffset - allowance &&
            currentPosition < offsetTop + pageTopOffset + offsetHeight
          ) {
            activeSection = section;
          }
        }
      });

      setActiveSection(activeSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [trackedSections]);

  return (
    <PageSectionContext.Provider
      value={{
        activeSection,
        sections: trackedSections,
        addSection,
        topOffset: pageTopOffset,
      }}
    >
      {children}
    </PageSectionContext.Provider>
  );
};

/*
 * TRACKED_SECTION_WRAPPER
 */
type TrackedSectionProps = Section &
  BoxProps & {
    isTracked?: boolean;
  };

export const TrackedSection: React.FC<TrackedSectionProps> = ({
  id,
  label,
  children,
  isTracked = true,
  ...props
}) => {
  const { addSection, topOffset } = useCurrentSection();

  useEffect(() => {
    if (isTracked) {
      addSection({ id, label });
    }
  }, [isTracked]);

  return (
    <TrackedSectionBox
      scrollTopOffset={topOffset}
      id={id}
      as="section"
      {...props}
    >
      {children}
    </TrackedSectionBox>
  );
};

const TrackedSectionBox = styled(Box)<{ scrollTopOffset: number }>`
  scroll-margin-top: ${({ scrollTopOffset }) => scrollTopOffset}px;
`;

export const useCurrentSection = () => {
  const context = useContext(PageSectionContext);
  if (!context) {
    throw new Error("useCurrentSection is used outside of it's provider");
  }
  return context;
};

const PageSections = {
  Provider: PageSectionProvider,
  TrackedSection: TrackedSection,
};

export default PageSections;
